import React, { useState, useEffect } from "react";
import { X, Tag, Check, Loader2, AlertCircle } from "lucide-react";
import { api, getErrorMessage } from "../../lib/api";
import { toast } from "sonner";

// Prices must match backend PLAN_CONFIGS and TOPUP_BUNDLES exactly
const PLAN_LABELS = {
  free: "Free", starter: "Starter", basic: "Basic",
  pro: "Pro", professional: "Professional", business: "Business",
};
const PLAN_PRICES  = { free: 0, starter: 499, basic: 499, pro: 1999, professional: 1999, business: 4999 };
const PLAN_CHARS   = { free: 5000, starter: 40000, basic: 40000, pro: 200000, professional: 200000, business: 560000 };

const BUNDLE_LABELS = {
  starter_mini:  "Mini (50k chars)",  starter_small: "Small (100k chars)", starter_large: "Large (200k chars)",
  premium_mini:  "Mini (50k chars)",  premium_small: "Small (100k chars)", premium_large: "Large (200k chars)",
};
const BUNDLE_PRICES = {
  starter_mini: 249, starter_small: 499, starter_large: 999,
  premium_mini: 449, premium_small: 899, premium_large: 1799,
};
const BUNDLE_CHARS = {
  starter_mini: 50000, starter_small: 100000, starter_large: 200000,
  premium_mini: 50000, premium_small: 100000, premium_large: 200000,
};

function fmt(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000)    return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

export default function CheckoutModal({ checkout, onClose, onSuccess }) {
  // checkout: { type, plan?, bundle_id?, amount?, chars_per_bdt? }
  const { type, plan, bundle_id, amount: customAmount, chars_per_bdt: cpb } = checkout || {};

  let label = "", baseAmount = 0, chars = 0, contextKey = "all";
  if (type === "plan") {
    label       = `${PLAN_LABELS[plan] || plan} Plan (monthly)`;
    baseAmount  = PLAN_PRICES[plan] || 0;
    chars       = PLAN_CHARS[plan]  || 0;
    contextKey  = plan;
  } else if (type === "topup") {
    label       = `Top-up: ${BUNDLE_LABELS[bundle_id] || bundle_id}`;
    baseAmount  = BUNDLE_PRICES[bundle_id] || 0;
    chars       = BUNDLE_CHARS[bundle_id]  || 0;
    contextKey  = bundle_id;
  } else if (type === "custom") {
    const rate  = cpb || 200;
    chars       = Math.round(customAmount * rate);
    baseAmount  = customAmount;
    label       = `Custom Top-up: ${fmt(chars)} characters`;
    contextKey  = "custom";
  }

  const [couponCode,    setCouponCode]    = useState("");
  const [couponState,   setCouponState]   = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError,   setCouponError]   = useState("");
  const [loading,       setLoading]       = useState(false);

  const finalAmount   = couponState ? couponState.final_amount   : baseAmount;
  const discountAmount = couponState ? couponState.discount_amount : 0;

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  async function handleValidateCoupon() {
    if (!couponCode.trim()) return;
    setCouponLoading(true); setCouponError(""); setCouponState(null);
    try {
      const { data } = await api.post("/billing/validate-coupon", {
        code: couponCode.trim(), context_key: contextKey, amount: baseAmount,
      });
      setCouponState(data);
    } catch (err) {
      setCouponError(getErrorMessage(err) || "Invalid coupon");
    } finally { setCouponLoading(false); }
  }

  async function handleProceed() {
    if (!type) return;
    setLoading(true);
    try {
      const body = { type };
      if (type === "plan")   body.plan      = plan;
      if (type === "topup")  body.bundle_id = bundle_id;
      if (type === "custom") body.amount    = customAmount;
      if (couponState)       body.coupon_code = couponState.code || couponCode.trim().toUpperCase();

      const { data } = await api.post("/billing/initiate-payment", body);
      if (data.direct) {
        toast.success("Applied! Your account has been updated.");
        onSuccess?.();
        onClose();
        return;
      }
      if (data.payment_url) window.location.href = data.payment_url;
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not initiate payment");
    } finally { setLoading(false); }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-cream-300">
          <div>
            <h2 className="font-display text-[20px] font-semibold text-ink">Order Summary</h2>
            <p className="text-[12px] text-ink-muted mt-0.5">Review before proceeding to payment</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-cream-100 text-ink-muted hover:text-ink transition">
            <X size={16}/>
          </button>
        </div>

        <div className="px-7 py-6 space-y-5">
          {/* Item */}
          <div className="bg-cream-100 rounded-2xl p-5 space-y-1">
            <p className="font-semibold text-[16px] text-ink">{label}</p>
            {chars > 0 && (
              <p className="text-[13px] text-ink-muted">
                {fmt(chars)} characters &nbsp;·&nbsp; ~{Math.floor(chars / 1000).toLocaleString()} articles
              </p>
            )}
          </div>

          {/* Coupon */}
          <div>
            <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-[0.18em] mb-2">Coupon code</p>
            <div className="flex gap-2">
              <input
                placeholder="e.g. WELCOME20"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState(null); setCouponError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleValidateCoupon()}
                disabled={!!couponState}
                className="flex-1 px-3.5 py-2.5 border border-cream-300 rounded-xl text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-coral/40 placeholder:text-ink-muted/60 disabled:opacity-60"
              />
              {couponState ? (
                <button onClick={() => { setCouponState(null); setCouponCode(""); setCouponError(""); }}
                  className="px-4 py-2.5 border border-cream-300 rounded-xl text-[12px] font-semibold text-ink-muted hover:bg-cream-100 transition">
                  Remove
                </button>
              ) : (
                <button onClick={handleValidateCoupon} disabled={couponLoading || !couponCode.trim()}
                  className="px-4 py-2.5 bg-ink text-cream-100 rounded-xl text-[12px] font-semibold hover:bg-coral transition disabled:opacity-40 flex items-center gap-1.5 min-w-[80px] justify-center">
                  {couponLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Tag size={12}/> Apply</>}
                </button>
              )}
            </div>
            {couponError && (
              <p className="text-red-500 text-[12px] mt-2 flex items-center gap-1"><AlertCircle size={12}/> {couponError}</p>
            )}
            {couponState && (
              <p className="text-[12px] mt-2 flex items-center gap-1" style={{color:"#2EE8B5"}}>
                <Check size={12}/>
                {couponState.discount_type === "percentage"
                  ? `${couponState.discount_value}% off applied`
                  : `৳${couponState.discount_value} off applied`}
              </p>
            )}
          </div>

          {/* Totals */}
          <div className="space-y-2 text-[14px] border-t border-cream-300 pt-4">
            <div className="flex justify-between text-ink-muted">
              <span>Subtotal</span>
              <span>৳{baseAmount.toLocaleString()}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between" style={{color:"#2EE8B5"}}>
                <span>Discount ({couponState.code})</span>
                <span>−৳{discountAmount.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-ink font-bold text-[16px] pt-1 border-t border-cream-300">
              <span>Total</span>
              <span>৳{finalAmount.toLocaleString()}</span>
            </div>
            {finalAmount <= 0 && baseAmount > 0 && (
              <p className="text-center text-[12px]" style={{color:"#2EE8B5"}}>100% discount — no payment required!</p>
            )}
          </div>

          {/* Proceed button */}
          <button
            onClick={handleProceed}
            disabled={loading}
            className="w-full py-3.5 rounded-2xl bg-coral text-white font-semibold text-[15px] hover:bg-coral/90 transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin"/> Processing…</>
            ) : finalAmount <= 0 ? (
              "Apply & Continue"
            ) : (
              `Pay ৳${finalAmount.toLocaleString()} →`
            )}
          </button>

          <p className="text-center text-[11px] text-ink-muted">
            Secure payment via EPS
          </p>
        </div>
      </div>
    </div>
  );
}
